import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { Paperclip, Send, Link2, X, Phone, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../ui/popover";
import { FILE_ACCEPT } from "../../lib/fileTypes";

const ChatInput = forwardRef(function ChatInput(
  { onSend, onUpload, onUrl, attached, files, onUnattach, disabled, injectedText,
    onStartVoice, preparingCorpus },
  ref,
) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Expose focus() to parent (used after sample-prompt clicks)
  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus?.(),
  }), []);

  // Parent can push a prompt into the textarea via `injectedText`
  useEffect(() => {
    if (injectedText) {
      setText(injectedText);
      // place caret at end + focus
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          try { ta.setSelectionRange(injectedText.length, injectedText.length); } catch {}
        }
      });
    }
  }, [injectedText]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!text.trim() || disabled) return;
    onSend(text);
    setText("");
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onUpload(f);
    e.target.value = "";
  };

  const attachedFiles = files.filter((f) => attached.includes(f.id));

  return (
    <div className="border-t border-border bg-background p-3 lg:p-4">
      <div className="max-w-3xl mx-auto">
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="attached-list">
            {attachedFiles.map((f) => (
              <div
                key={f.id}
                className="inline-flex items-center gap-1.5 border border-border bg-secondary text-xs font-mono px-2 py-1 rounded-sm"
              >
                <span className="truncate max-w-[180px]">{f.name}</span>
                <button onClick={() => onUnattach(f.id)} aria-label={t("workspace:thread.detach")}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={submit} className="flex items-end gap-2 border border-border bg-card rounded-sm focus-within:ring-2 focus-within:ring-primary p-2">
          <div className="flex gap-1 pb-1">
            <input
              type="file"
              ref={fileInputRef}
              hidden
              accept={FILE_ACCEPT}
              onChange={handleFileChange}
              data-testid="hidden-file-input"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid="attach-file-button"
              aria-label={t("workspace:thread.attach_aria")}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-sm"
                  data-testid="attach-url-button"
                  aria-label={t("workspace:thread.attach_url_aria")}
                >
                  <Link2 className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{t("workspace:thread.add_url")}</div>
                  <Input
                    placeholder={t("workspace:thread.url_placeholder")}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    data-testid="url-input"
                    className="rounded-sm"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      if (url.trim()) {
                        onUrl(url.trim());
                        setUrl("");
                      }
                    }}
                    size="sm"
                    className="w-full rounded-sm"
                    data-testid="url-submit-button"
                  >
                    {t("workspace:thread.add_url_btn")}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKey}
            placeholder={disabled ? t("workspace:thread.input_disabled") : t("workspace:thread.compose_placeholder")}
            rows={1}
            data-testid="chat-input-textarea"
            className="flex-1 min-h-[40px] max-h-40 resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-1 py-2 shadow-none"
          />
          <Button
            type="button"
            size="icon"
            variant="outline"
            disabled={preparingCorpus || !onStartVoice}
            onClick={onStartVoice}
            data-testid="open-voice-button"
            className="h-8 w-8 rounded-sm"
            aria-label={t("workspace:thread.call_aria", "Iniciar llamada de voz")}
            title={t("workspace:thread.call_aria", "Iniciar llamada de voz")}
          >
            {preparingCorpus ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Phone className="h-4 w-4" />
            )}
          </Button>
          <Button
            type="submit"
            size="icon"
            disabled={disabled || !text.trim()}
            data-testid="chat-send-button"
            className="h-8 w-8 rounded-sm"
            aria-label={t("workspace:thread.send_aria")}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
        <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">
          {t("workspace:thread.input_hint", { kbd: "⌘K" })}
        </div>
      </div>
    </div>
  );
});

export default ChatInput;
